import { useContext } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Switch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Loading from 'components/Loading'
import Paper from 'components/Paper'
import { useGetWorkspacePrebidConfiguration } from 'features/globalSetup'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import ProductAdUnitsTable from '../../components/ProductAdUnitsTable'
import { AddEditDomainContext } from '../../contexts/AddEditDomainContext'
import { Schema } from './schema'
import { AdvancedPrebidSection } from './sections/AdvancedPrebidSection'
import { BiddersSection } from './sections/BiddersSection'
import { ConsentManagementSection } from './sections/ConsentManagementSection'
import { InventoryAmazonUamTamSection } from './sections/InventoryAmazonUamTamSection'
import { ModulesSection } from './sections/ModulesSection'
import { NonConsentTrafficMonetizationSection } from './sections/NonConsentTrafficMonetizationSection'
import { TimeoutSection } from './sections/TimeoutSection'

export const Form = (): JSX.Element => {
  const ability = useAbility()
  const { t } = useTranslation(['features/domain', 'common'])
  const { domain } = useContext(AddEditDomainContext)
  const { control } = useFormContext<Schema>()

  const { workspaceId } = useWorkspaceParam()
  const { workspacePrebidConfiguration } = useGetWorkspacePrebidConfiguration({
    workspaceId,
  })

  if (!workspacePrebidConfiguration) {
    return <Loading />
  }

  if (!domain) {
    throw Error('Domain should be accessible')
  }

  const {
    domainPrebidStack: { domainAmazonEnabled },
    id,
  } = domain

  const cannotUpdateDomain: boolean = ability.cannot('update', 'Domain')
  const cannotUpdatePrebidStack: boolean = ability.cannot('update', 'Product-prebidStack')

  return (
    <>
      <Paper>
        <SectionHeader
          subtitle={t('form.prebidStack.enabled.description')}
          title={t('form.prebidStack.enabled.title')}
          titleEndAdornment={
            <Switch
              control={control}
              disabled={cannotUpdateDomain || cannotUpdatePrebidStack}
              name='prebidStackEnabled'
            />
          }
        />
      </Paper>

      <InventoryAmazonUamTamSection<Schema> amazonState={domainAmazonEnabled} />

      <TimeoutSection />

      <ConsentManagementSection {...{ workspacePrebidConfiguration }} />

      <NonConsentTrafficMonetizationSection {...{ workspacePrebidConfiguration }} />

      <AdvancedPrebidSection {...{ workspacePrebidConfiguration }} />

      <Controller
        control={control}
        name='domainBiddersAttributes'
        render={() => <BiddersSection />}
      />

      <Controller
        control={control}
        name='domainPrebidModulesAttributes'
        render={() => <ModulesSection />}
      />

      <Controller
        control={control}
        name='adUnitsAttributes'
        render={() => (
          <ProductAdUnitsTable
            domainId={id}
            product='prebidStack'
          />
        )}
      />
    </>
  )
}
